import { S25Util } from "../../../util/s25-util";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";

export class ImportUtil {
    public static getDateTimeStr(
        startISO8601Str: string,
        endISO8601Str: string,
        timeFormat: string,
        dateTimeFormat: string,
    ): string {
        // if same day date, only show date on end
        const startDt = S25Util.date.parse(startISO8601Str);
        const endDt = S25Util.date.parse(endISO8601Str);
        if (!startDt || !endDt) {
            return "Unknown Date Range";
        }
        if (S25Util.date.equalDate(startDt, endDt)) {
            return (
                S25Datefilter.transform(startDt, dateTimeFormat) + " - " + S25Datefilter.transform(endDt, timeFormat)
            );
        }
        return (
            S25Datefilter.transform(startDt, dateTimeFormat) + " - " + S25Datefilter.transform(endDt, dateTimeFormat)
        );
    }

    public static splitStr(value: string, delim: string) {
        return (
            (value &&
                delim &&
                value
                    .split(delim)
                    .filter((v) => !!v)
                    .filter((v) => !!v.trim())
                    .map((v) => v.trim())) ||
            []
        );
    }

    /**
     * Numeric cells are IDs. Non-numeric cells, or values prefixed with {@code $NAME-}, are names
     * (so a definition named {@code 123} can be imported as {@code $NAME-123}).
     */
    public static parseIdOrName(value: unknown): { id?: number; name?: string } {
        if (value == null) {
            return {};
        }
        const raw = String(value).trim();
        if (!raw) {
            return {};
        }
        if (raw.startsWith("$NAME-")) {
            const name = raw.replace(/^\$NAME-/, "").trim();
            return name ? { name } : {};
        }
        if (S25Util.isParsableAsInt(raw)) {
            const id = S25Util.parseInt(raw);
            return id != null ? { id } : { name: raw };
        }
        return { name: raw };
    }

    public static normalize(value: string): string {
        return value
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "") // remove non-alphanumeric characters
            .replace(/s$/, ""); // accept plural or singular
    }

    static readonly CA_PREFIX = "ca:";

    /** "CA: <B> Veteran" -> { name: "Veteran", dataType: "B" }; "CA: Veteran" -> { name: "Veteran" }. */
    static parseCustomAttributeHeader(header: string): { name: string; dataType?: string } | undefined {
        const afterPrefix = header.substring(this.CA_PREFIX.length).trim();
        if (!afterPrefix) {
            return undefined;
        }
        const typeMatch = afterPrefix.match(/^<\s*([A-Za-z0-9]+)\s*>\s*(.*)$/);
        if (typeMatch) {
            const dataType = typeMatch[1]?.trim() ?? "";
            const name = typeMatch[2]?.trim() ?? "";
            if (!name) {
                return undefined;
            }
            return { name, dataType };
        }
        return { name: afterPrefix };
    }

    /** Returns true when the raw header text starts with the custom-attribute prefix. */
    static isCustomAttributeHeader(header: string): boolean {
        return header.toLowerCase().startsWith(this.CA_PREFIX);
    }

    public static getMissingRequiredColumns(headers: Map<number, string>, requiredColumns: string[]): string[] {
        const normalizedHeaders = new Set<string>();
        headers.forEach((header) => {
            normalizedHeaders.add(String(this.normalize(String(header))));
        });

        const missing: string[] = [];
        requiredColumns.forEach((col) => {
            if (!normalizedHeaders.has(String(this.normalize(String(col))))) {
                missing.push(col);
            }
        });

        return missing;
    }
}
